import { useEffect } from 'react';
import type { SuiClient } from '@mysten/sui/client';
import { Ed25519Keypair } from '@mysten/sui/keypairs/ed25519';
import { Transaction } from '@mysten/sui/transactions';
import { toBase64 } from '@mysten/sui/utils';
import type {
	StandardConnectFeature,
	StandardConnectMethod,
	StandardEventsFeature,
	StandardEventsOnMethod,
	SuiFeatures,
	SuiSignAndExecuteTransactionBlockMethod,
	SuiSignAndExecuteTransactionMethod,
	SuiSignPersonalMessageMethod,
	SuiSignTransactionBlockMethod,
	SuiSignTransactionMethod,
	Wallet,
} from '@mysten/wallet-standard';
import { getWallets, ReadonlyWalletAccount, SUI_CHAINS } from '@mysten/wallet-standard';
import { useSuiClient } from '@mysten/dapp-kit';

import { getE2EWalletPrivateKeys, isE2EEnabled } from '@/utils/e2e';

const WALLET_NAME = 'Suigar E2E Wallet';

export function useE2ETestWallet() {
	const suiClient = useSuiClient();

	useEffect(() => {
		if (import.meta.env.VITE_E2E_TEST_WALLET !== '1') {
			return;
		}

		if (!isE2EEnabled()) {
			return;
		}

		const privateKeys = getE2EWalletPrivateKeys();
		if (!privateKeys.length) {
			return;
		}

		return registerE2ETestWallet(suiClient, privateKeys);
	}, [suiClient]);
}

function registerE2ETestWallet(suiClient: SuiClient, privateKeys: string[]) {
	const walletsApi = getWallets();
	const registeredWallets = walletsApi.get();

	if (registeredWallets.find((wallet) => wallet.name === WALLET_NAME)) {
		console.warn(`registerE2ETestWallet: ${WALLET_NAME} already registered; skipping.`);
		return;
	}

	const keypairs = privateKeys.map((key) => Ed25519Keypair.fromSecretKey(key));
	const keypairByAddress = new Map<string, Ed25519Keypair>();

	const accounts = keypairs.map((keypair) => {
		const address = keypair.getPublicKey().toSuiAddress();
		keypairByAddress.set(address, keypair);

		return new ReadonlyWalletAccount({
			address,
			publicKey: keypair.getPublicKey().toSuiBytes(),
			chains: SUI_CHAINS,
			features: [
				'sui:signAndExecuteTransactionBlock',
				'sui:signTransactionBlock',
				'sui:signTransaction',
				'sui:signAndExecuteTransaction',
				'sui:signPersonalMessage',
			],
		});
	});

	const resolveKeypair = (address: string | undefined | null): Ed25519Keypair => {
		const resolvedAddress = address ?? accounts[0]?.address ?? null;
		const keypair = resolvedAddress ? keypairByAddress.get(resolvedAddress) : null;
		if (!keypair) {
			throw new Error('E2E wallet misconfigured: missing signer keypair');
		}
		return keypair;
	};

	const signTransactionBlockUnsafe = async (transactionBlock: unknown, signer: Ed25519Keypair) => {
		// `@mysten/wallet-standard` bundles its own `@mysten/sui` types, which makes the `TransactionBlock.sign`
		// signature incompatible with our `@mysten/sui` client types at compile time, even though runtime works.
		const signed = await (transactionBlock as any).sign({ client: suiClient as any, signer: signer as any });
		return signed as { bytes: string; signature: string };
	};

	class E2EWallet implements Wallet {
		get version() {
			return '1.0.0' as const;
		}

		get name() {
			return WALLET_NAME;
		}

		get icon() {
			return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDY0IDY0Ij48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHJ4PSIxMiIgZmlsbD0iIzExMTgyNyIvPjxwYXRoIGQ9Ik0yMCAzOGM0IDYgMjAgNiAyNCAwIiBzdHJva2U9IiNGNTlFMEIiIHN0cm9rZS13aWR0aD0iNCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PHBhdGggZD0iTTIyIDI2aDZNMzYgMjZoNiIgc3Ryb2tlPSIjRTVFN0VCIiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjwvc3ZnPg==' as const;
		}

		get chains() {
			return SUI_CHAINS;
		}

		get accounts() {
			return accounts;
		}

		get features(): StandardConnectFeature & StandardEventsFeature & SuiFeatures {
			return {
				'standard:connect': {
					version: '1.0.0',
					connect: this.#connect,
				},
				'standard:events': {
					version: '1.0.0',
					on: this.#on,
				},
				'sui:signPersonalMessage': {
					version: '1.1.0',
					signPersonalMessage: this.#signPersonalMessage,
				},
				'sui:signTransactionBlock': {
					version: '1.0.0',
					signTransactionBlock: this.#signTransactionBlock,
				},
				'sui:signAndExecuteTransactionBlock': {
					version: '1.0.0',
					signAndExecuteTransactionBlock: this.#signAndExecuteTransactionBlock,
				},
				'sui:signTransaction': {
					version: '2.0.0',
					signTransaction: this.#signTransaction,
				},
				'sui:signAndExecuteTransaction': {
					version: '2.0.0',
					signAndExecuteTransaction: this.#signAndExecuteTransaction,
				},
			};
		}

		#on: StandardEventsOnMethod = () => {
			return () => {};
		};

		#connect: StandardConnectMethod = async () => {
			return { accounts: this.accounts };
		};

		#signPersonalMessage: SuiSignPersonalMessageMethod = async (messageInput) => {
			const keypair = resolveKeypair(messageInput.account?.address);
			const { bytes, signature } = await keypair.signPersonalMessage(messageInput.message);
			return { bytes, signature };
		};

		#signTransactionBlock: SuiSignTransactionBlockMethod = async (transactionInput) => {
			const keypair = resolveKeypair(transactionInput.account?.address);
			const { bytes, signature } = await signTransactionBlockUnsafe(transactionInput.transactionBlock, keypair);

			return {
				transactionBlockBytes: bytes,
				signature,
			};
		};

		#signTransaction: SuiSignTransactionMethod = async (transactionInput) => {
			const keypair = resolveKeypair(transactionInput.account?.address);
			const { bytes, signature } = await Transaction.from(await transactionInput.transaction.toJSON()).sign({
				client: suiClient,
				signer: keypair,
			});

			transactionInput.signal?.throwIfAborted();

			return { bytes, signature };
		};

		#signAndExecuteTransactionBlock: SuiSignAndExecuteTransactionBlockMethod = async (transactionInput) => {
			const keypair = resolveKeypair(transactionInput.account?.address);
			const { bytes, signature } = await signTransactionBlockUnsafe(transactionInput.transactionBlock, keypair);

			return suiClient.executeTransactionBlock({
				signature,
				transactionBlock: bytes,
				options: transactionInput.options,
			});
		};

		#signAndExecuteTransaction: SuiSignAndExecuteTransactionMethod = async (transactionInput) => {
			const keypair = resolveKeypair(transactionInput.account?.address);
			const { bytes, signature } = await Transaction.from(await transactionInput.transaction.toJSON()).sign({
				client: suiClient,
				signer: keypair,
			});

			transactionInput.signal?.throwIfAborted();

			const { rawEffects, digest } = await suiClient.executeTransactionBlock({
				signature,
				transactionBlock: bytes,
				options: { showRawEffects: true },
			});

			return {
				bytes,
				signature,
				digest,
				effects: toBase64(new Uint8Array(rawEffects!)),
			};
		};
	}

	return walletsApi.register(new E2EWallet());
}
